// Real-time Admin Actions Component
// Handles logging and tracking of admin actions in real-time

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Clock, 
  User, 
  Settings, 
  Database,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { formatTimestamp } from '../../utils/timestampUtils';

const RealTimeAdminActions = () => {
  const [error, setError] = useState(null);
  const [isLogging, setIsLogging] = useState(false);
  
  // Safely get realtime data
  let adminData = {};
  let logAdminAction = null;
  
  try {
    const realtimeData = useRealtime();
    adminData = realtimeData.adminData || {};
    logAdminAction = realtimeData.logAdminAction;
  } catch (error) {
    setError('Real-time features unavailable');
  }

  // Log admin action with real-time feedback
  const logAction = async (action, details) => {
    if (!logAdminAction) {
      return;
    }
    
    setIsLogging(true);
    try {
      const result = await logAdminAction(action, details);
      if (result.success) {
        } else {
        setError('Failed to log admin action');
      }
    } catch (error) {
      setError('Error logging admin action');
    } finally {
      setIsLogging(false);
    }
  };

  // Common admin actions
  const commonActions = [
    {
      title: 'User Management',
      actions: [
        { name: 'Create User', action: 'user_created', details: 'New user account created' },
        { name: 'Update User', action: 'user_updated', details: 'User profile updated' },
        { name: 'Delete User', action: 'user_deleted', details: 'User account deleted' },
        { name: 'Block User', action: 'user_blocked', details: 'User account blocked' }
      ]
    },
    {
      title: 'Content Management',
      actions: [
        { name: 'Create Test', action: 'test_created', details: 'New mock test created' },
        { name: 'Update Test', action: 'test_updated', details: 'Test content updated' },
        { name: 'Delete Test', action: 'test_deleted', details: 'Test removed' },
        { name: 'Create Course', action: 'course_created', details: 'New course created' },
        { name: 'Update Course', action: 'course_updated', details: 'Course content updated' }
      ]
    },
    {
      title: 'System Management',
      actions: [
        { name: 'System Settings', action: 'settings_updated', details: 'System configuration changed' },
        { name: 'Data Migration', action: 'migration_started', details: 'Data migration initiated' },
        { name: 'Backup Created', action: 'backup_created', details: 'System backup completed' },
        { name: 'Analytics Export', action: 'analytics_exported', details: 'Analytics data exported' }
      ]
    }
  ];

  // Show error state if there's an error
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-time Features Unavailable</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-2 bg-blue-100 rounded-lg mr-3">
            <Activity className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Real-time Admin Actions</h2>
            <p className="text-sm text-gray-600">Log and track admin activities</p>
          </div>
        </div>
        
        {isLogging && (
          <div className="flex items-center text-blue-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-sm">Logging...</span>
          </div>
        )}
      </div>

      {/* Action Categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {commonActions.map((category, categoryIndex) => (
          <motion.div
            key={category.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: categoryIndex * 0.1 }}
            className="bg-gray-50 rounded-lg p-4"
          >
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              {category.title === 'User Management' && <User className="h-4 w-4 mr-2" />}
              {category.title === 'Content Management' && <Database className="h-4 w-4 mr-2" />}
              {category.title === 'System Management' && <Settings className="h-4 w-4 mr-2" />}
              {category.title}
            </h3>
            
            <div className="space-y-2">
              {category.actions.map((action, actionIndex) => (
                <motion.button
                  key={action.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (categoryIndex * 0.1) + (actionIndex * 0.05) }}
                  onClick={() => logAction(action.action, action.details)}
                  disabled={isLogging}
                  className="w-full text-left p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{action.name}</span>
                    <Info className="h-4 w-4 text-gray-400" />
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Recent Admin Actions */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Clock className="h-4 w-4 mr-2" />
          Recent Admin Actions
        </h3>
        
        <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
          {adminData?.adminActions && Array.isArray(adminData.adminActions) && adminData.adminActions.length > 0 ? (
            <div className="space-y-3">
              {adminData.adminActions.slice(0, 10).filter(action => action && typeof action === 'object').map((action, index) => (
                <motion.div
                  key={action.id || index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                >
                  <div className="flex items-center">
                    <div className="p-1 bg-green-100 rounded mr-3">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {(() => {
                          try {
                            if (typeof action.action === 'string') {
                              return action.action.replace('_', ' ').toUpperCase();
                            } else if (typeof action.action === 'object' && action.action !== null) {
                              if (action.action.type) {
                                return `ACTION: ${action.action.type.toUpperCase()}`;
                              } else if (action.action.name) {
                                return `ACTION: ${action.action.name.toUpperCase()}`;
                              } else {
                                return 'ADMIN ACTION';
                              }
                            } else {
                              return 'ADMIN ACTION';
                            }
                          } catch (error) {
                            return 'ADMIN ACTION';
                          }
                        })()}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(() => {
                          try {
                            if (typeof action.details === 'string') {
                              return action.details;
                            } else if (typeof action.details === 'object' && action.details !== null) {
                              return 'Action details available';
                            } else {
                              return 'No details available';
                            }
                          } catch (error) {
                            return 'No details available';
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">
                      {(() => {
                        try {
                          if (action.timestamp) {
                            return formatTimestamp(action.timestamp, { short: true });
                          } else {
                            return 'No timestamp';
                          }
                        } catch (error) {
                          return 'No timestamp';
                        }
                      })()}
                    </p>
                    <p className="text-xs text-gray-400">Admin</p>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No admin actions logged yet</p>
              <p className="text-gray-400 text-xs mt-1">Actions will appear here in real-time</p>
            </div>
          )}
        </div>
      </div>

      {/* Real-time Status */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center">
          <div className="p-1 bg-blue-100 rounded mr-3">
            <Activity className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900">Real-time Logging Active</p>
            <p className="text-xs text-blue-700">
              All admin actions are automatically logged and synchronized across all admin sessions
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeAdminActions;
